<template>
  <RouteWithChildren :parent-level="parentLevel">
    <ItemDetail
      ref="itemDetail"
      :item-title="asset ? asset.name : ''"
      :item="asset || {}"
      :rows="rows"
      :is-loading="isLoading"
      :is-loading-in-background="isLoadingInBackground"
      :save-item="save"
      :delete-item="deleteAsset"
      :delete-tracker="deleteLabel"
      :delete-item-dialogue-title="deleteAssetDialogueTitle"
      :delete-tracker-dialogue-title="deleteLabelDialogueTitle"
      :error="errorMessage"
      :display-edit="fromFacility? false: true"
      :explorer-toggle-options="explorerToggleOptions"
      @close="close"
      @startEditing="() => isEditing = true"
      @stopEditing="() => isEditing = false"
    />
    <ButtonsDialogue
      :is-visible="isLinkingLabelDialogueVisible"
      :title="linkLabelDialogueTitle"
      :message="linkLabelDialogueMessage"
    >
      <Button
        v-if="isYesButtonVisible"
        :is-loading="isYesButtonLoading"
        text="Yes"
        is-warning
        :on-click="replaceCurrentLabel"
      />
      <Button
        v-if="isRedirectButtonVisible"
        :text="redirectButtonText"
        :on-click="redirectClicked"
      />
      <Button
        :text="dismissDialogueButtonText"
        :on-click="resetLinkingDialogue"
      />
    </ButtonsDialogue>
  </RouteWithChildren>
</template>

<script>
import {
  linkTrackerStatus, routeNames, facilityTabSlugs, itemDetailTabSlugs,
  locationStateDisplayNames, onlineStates, temperatureUnits, getAlertIcon,
  getAlertDisplayName,
  alertCategories,
  mapItemTypes,
} from '../../constants';
import RouteWithChildren from '../../components/organisms/layout/route-with-children';
import ItemDetail from '../../components/organisms/items/item-detail';
import linkLabelHelper from '../../mixins/link-label-helper';
import dateTimeHelper from '../../mixins/date-time-helper';
import updateDateTimes from '../../mixins/update-date-times';
import ButtonsDialogue from '../../components/molecules/dialogues/dialogue-with-buttons';

import AssetTimeline from '../../components/organisms/assets/asset-timeline';
import MapWithPopup from '../../components/molecules/map/map-with-popup';
import AssetTemperatureZonesChart from '../../components/organisms/assets/asset-temperature-zones-chart';

import Button from '../../components/atoms/button/button';
import constructAssetCurrentlyIn from '../../helpers/currently-in-helper';
import { shipments } from '../../data/shipments-demo';

function constructTemperatureThresholdDisplay(thresholds) {
  if (!Array.isArray(thresholds) || thresholds.length === 0) {
    return 'No thresholds set';
  }

  // min and max are optional
  return thresholds.map((t) => {
    const min = t.min !== undefined && t.min !== null ? `Min ${t.min}${temperatureUnits.celsius}` : '';
    const max = t.max !== undefined && t.max !== null ? `Max ${t.max}${temperatureUnits.celsius}` : '';
    return [min, max].filter(Boolean).join(', ');
  }).join(' | ');
}

export default {
  name: 'AssetDetail',
  components: {
    RouteWithChildren,
    ItemDetail,
    ButtonsDialogue,
    Button,
  },
  mixins: [linkLabelHelper, dateTimeHelper, updateDateTimes],
  props: {
    id: { type: String, required: true },
    isFacilityAssetDetail: { type: Boolean, default: false },
    isPrintJobAssetDetail: { type: Boolean, default: false },
    resetTemperatureData: { type: Boolean, default: false },
  },
  data() {
    return {
      isRedirectButtonVisible: false,
      redirectButtonText: 'View Asset',
      isYesButtonVisible: false,
      isYesButtonLoading: false,
      isLinkingLabelDialogueVisible: false,
      linkLabelDialogueTitle: '',
      linkLabelDialogueMessage: '',
      dismissDialogueButtonText: 'Cancel',
      deleteAssetDialogueTitle: 'Are you sure you want to delete this asset?',
      scannedLabelForAssetId: null,
      scannedLabelForTeamId: null,
      loadingDataForItemIds: [],
      isEditing: false,
    };
  },
  computed: {
    parentLevel() {
      if (this.isPrintJobAssetDetail) {
        return 5;
      }
      if (this.isFacilityAssetDetail) {
        return 4;
      }
      return 3;
    },
    fromFacility() { return this.isFacilityAssetDetail || this.isPrintJobAssetDetail; },
    explorerToggleOptions() {
      const options = [];
      if (this.includeTimeline) {
        options.push({
          displayName: 'Timeline',
          slug: itemDetailTabSlugs.timeline,
          component: AssetTimeline,
          componentProps: {
            assetId: this.id,
            displayLocationTimeline: this.displayLocationTimeline,
          },
        });
      }
      if (this.hasLocation) {
        options.push({
          displayName: 'Map',
          slug: itemDetailTabSlugs.map,
          component: MapWithPopup,
          componentProps: {
            isLoading: this.isLoadingAsset,
            fitToNewData: true,
            locations: this.locations,
            timer: this.timer,
            hasMoreLocations: this.hasMoreLocations,
            alerts: Array.isArray(this.alerts)
              && this.alerts.length > 0
              && this.locations.some((loc) => this.alerts
                .some((a) => a.timestamp === loc.timestamp))
              ? this.locations
                .filter((loc) => this.alerts
                  .some((a) => a.timestamp === loc.timestamp))
                .map((loc) => {
                  const alert = (this.asset.alerts || [])
                    .find((a) => a.timestamp === loc.timestamp);
                  return {
                    ...loc,
                    properties: {
                      type: mapItemTypes.alert,
                      name: getAlertDisplayName(alert.category, alert.type),
                      icon: getAlertIcon(alert.category, alert.type),
                      iconColor: alert.hasEnded ? '#EAB839' : 'warning',
                    },
                  };
                })
              : [],
          },
        });
      }
      if (this.hasTemperatureData) {
        options.push({
          displayName: 'Temperature',
          slug: itemDetailTabSlugs.temperature,
          component: AssetTemperatureZonesChart,
          componentProps: {
            assetId: this.id,
            isAssetDataLoading: this.isLoading || this.isLoadingInBackground,
          },
        });
      }
      return options;
    },
    locations() {
      if (!this.item?.locations?.items) {
        return [];
      }
      return this.item.locations.items
        .map(({
          longitude, latitude, timestamp, error, updatedAt,
          isAlert,
        }) => ({
          longitude,
          latitude,
          error,
          timestamp,
          isAlert,
          properties: {
            id: this.item.id,
            name: this.item.name,
            timestamp: updatedAt || timestamp,
          },
        }));
    },
    hasLocation() {
      return this.locations.length > 0;
    },
    asset() {
      return this.$store.state.assets.assets.find(({ id }) => id === this.id);
    },
    alerts() {
      if (this.isShipmentDemo) {
        return this.asset?.alerts || [];
      }

      return this.asset?.activeAlerts || [];
    },
    item() { return this.asset || {}; },
    hasTrackingUi() { return this.item.hasTrackingUi; },
    assetIsLoaded() {
      return Object.keys(this.item).length > 0;
    },
    itemAlreadyLoaded() {
      return typeof this.asset !== 'undefined';
    },
    assetHasLabel() {
      return this.asset && this.asset.bleId !== null;
    },
    isTemperatureLabel() {
      return !!this.asset?.temperatures;
    },
    hasTemperatureData() {
      return this.asset?.temperatures?.items?.length;
    },
    displayLocationTimeline() {
      return (!this.$root.workspaceFeatures.assetSpaceHistoryTimeline
        || ((this.$store.state.assets.assetInSpaces
          .assetsData[this.id] || {}).items || []).length === 0)
        && (((this.asset || {}).locations || {}).items || []).filter((l) => !!l.address).length > 0;
    },
    includeTimeline() {
      return !this.hasError
        && (this.$root.workspaceFeatures.assetSpaceHistoryTimeline
        || this.$root.workspaceFeatures.assetLocationTimeline);
    },
    hasMoreLocations() {
      return ((this.asset || {}).locations || {}).nextToken !== null;
    },
    scannedLabel() {
      return this.$store.state.mobileApp.label;
    },
    scannedGateway() {
      return this.$store.state.mobileApp.gateway;
    },
    deleteLabelDialogueTitle() {
      return this.itemAlreadyLoaded
        ? `Are you sure you would like to delete label from "${this.asset.name}"?`
        : '';
    },
    isLoadingAsset() { return this.$store.state.assets.isLoadingAsset; },
    isLoading() {
      return !this.isLoadingInBackground && this.isLoadingAsset;
    },
    isLoadingInBackground() {
      return this.loadingDataForItemIds.length > 0;
    },
    isShipmentDemo() { return this.$root.workspaceFeatures.shipmentsDemo; },
    shipmentScreensEnabled() { return this.$root.workspaceFeatures.shipmentScreens; },
    includeShipment() {
      return (this.isShipmentDemo && !!this.asset.consignmentId)
        || this.asset.consignment;
    },
    displayAddAssetLinkLabelButtons() {
      return this.$root.featureToggles.includeAddAssetLinkLabelButtons();
    },
    rows() {
      if (!this.itemAlreadyLoaded) return [];
      const labelRowButtons = [];

      if (this.displayAddAssetLinkLabelButtons) {
        labelRowButtons.push({
          props: {
            text: 'Link Label',
            isText: true,
            isSmall: true,
            onClick: this.linkLabel,
            disabled: this.$root.isViewer,
          },
        });
      }

      if (this.assetHasLabel && this.displayAddAssetLinkLabelButtons) {
        labelRowButtons.push({
          props: {
            text: 'Delete',
            isText: true,
            isWarning: true,
            isSmall: true,
            onClick: () => this.$refs.itemDetail.displayDeleteTrackerDialogue(),
            disabled: this.$root.isViewer,
          },
        });
      }

      const rows = [
        {
          // need to include the timer, so that the computed property is re-computed every 10 secs
          timer: this.isEditing ? null : this.timer,
          name: 'Name',
          value: this.asset.name,
          isEditable: true,
          buttons: !this.isEditing && this.hasTrackingUi
            ? [{
              props: {
                text: 'View Tracking Screen',
                isText: true,
                isSmall: true,
                onClick: this.viewNbIotTrackingScreen,
              },
            }]
            : [],
        },
      ];

      if (!this.fromFacility) {
        rows.push({
          name: 'Has label',
          value: this.assetHasLabel,
          buttons: labelRowButtons,
        });
      }

      rows.push({
        name: 'Currently in',
        value: constructAssetCurrentlyIn(
          this.asset,
          'Not in a zone',
        ),
      });

      if (this.includeShipment) {
        const consignmentAlerts = this.alerts
          .filter((a) => !a.hasEnded && a.category === alertCategories.consignment);
        rows.push({
          name: 'Shipment',
          value: {
            value: this.isShipmentDemo
              ? shipments.find((s) => s.id === this.asset.consignmentId).trackingRef
              : this.asset.consignment.trackingRef,
            icon: consignmentAlerts.length > 0
              ? getAlertIcon(consignmentAlerts[0].category, consignmentAlerts[0].type)
              : '',
            iconColor: 'warning',
            iconSize: '20',
          },
          buttons: [
            ...(this.shipmentScreensEnabled || this.isShipmentDemo
              ? [{
                props: {
                  text: 'View Shipment',
                  isText: true,
                  isSmall: true,
                  onClick: () => this.$router.push({
                    name: routeNames.shipments.detail,
                    params: {
                      id: this.isShipmentDemo
                        ? this.asset.consignmentId
                        : this.asset.consignment.id,
                    },
                    query: {
                      tab: itemDetailTabSlugs.assets,
                    },
                  }),
                },
              }]
              : []),
          ],
        });
      }

      if ((this.asset.status || {}).onlineState || (this.asset.status || {}).motionState) {
        const status = (this.asset.status || {});
        let statusValue = `${status.onlineState || ''}`;
        if (status.onlineState === onlineStates.online) {
          statusValue += `, ${locationStateDisplayNames[status.motionState] || ''}`;
        }
        rows.push({ name: 'Status', value: statusValue });
      }

      const firstSeen = this.getFirstSeen(this.asset);
      if (firstSeen) {
        rows.push({ name: 'First seen', value: firstSeen });
      }
      rows.push({ name: 'Last seen', value: this.getLastSeen(this.asset) || 'No data' });

      if (this.isTemperatureLabel) {
        const buttons = [];
        if (this.$root.featureToggles.includeTemperatureGetData(this.$store.state.mobileApp)) {
          buttons.push({
            props: {
              text: 'Get data',
              isText: true,
              isSmall: true,
              onClick: this.getLabelTemperatureData,
              disabled: this.$root.isViewer,
            },
          });
        }
        const temperatureAlerts = this.alerts
          .filter((a) => !a.hasEnded && a.category === alertCategories.temperature);

        rows.push({
          name: 'Temperature',
          value: {
            value: this.hasTemperatureData
              ? `${this.asset.temperatures.items[0].degreesCelcius.toFixed(1)}${temperatureUnits.celsius}`
              : 'No data',
            icon: temperatureAlerts.length > 0
              ? getAlertIcon(temperatureAlerts[0].category, temperatureAlerts[0].type)
              : '',
            iconColor: 'warning',
            iconSize: '20',
          },
          buttons,
        });
      }

      if (Array.isArray(this.asset.temperatureThresholds)
          && this.asset.temperatureThresholds.length > 0) {
        rows.push({
          name: 'Threshold',
          value: constructTemperatureThresholdDisplay(this.asset.temperatureThresholds),
        });
      }

      if (this.asset.assetType) {
        rows.push({ name: 'Asset type', value: this.asset.assetType.name });
        rows.push(...(this.asset.assetType.fields || []).map((field) => ({
          name: field.name,
          value: field.value || (field.values || []).join(', '),
        })));
      }

      return rows;
    },
    showDeleteButton() {
      return this.assetHasLabel
        && (this.$store.getters['user/isAdmin'] || this.$store.getters['user/isOwner']);
    },
    errorMessage() {
      return this.$store.state.assets.errorMessage;
    },
    hasError() {
      return this.errorMessage !== '';
    },
    isMobileApp() {
      return this.$store.getters.isMobileApp;
    },
  },
  watch: {
    id() {
      this.getItemData();
    },
    isMobileApp() {
      this.startPolling();
    },
    async scannedLabel() {
      if (this.scannedLabel !== null) {
        this.resetLinkingDialogue(true);
        const labelStatus = await this.checkLabel(this.scannedLabel);
        switch (labelStatus.status) {
          case linkTrackerStatus.availableForLinking:
            if (this.assetHasLabel) {
              this.linkLabelDialogueTitle = 'Asset already has a label';
              this.linkLabelDialogueMessage = "Select 'yes' to replace the original label";
              this.isLinkingLabelDialogueVisible = true;
              this.isYesButtonVisible = true;
              this.dismissDialogueButtonText = 'No';
            } else {
              await this.addLabelToAsset(this.scannedLabel);
              this.resetLinkingDialogue();
            }
            break;
          case linkTrackerStatus.noAccess:
            this.linkLabelDialogueTitle = 'Error';
            this.linkLabelDialogueMessage = 'This label is linked to an asset you do not have access to';
            this.isLinkingLabelDialogueVisible = true;
            break;
          case linkTrackerStatus.alreadyLinked:
            this.linkLabelDialogueTitle = 'Label scanned';
            this.linkLabelDialogueMessage = 'This label is already linked to an asset';
            this.isRedirectButtonVisible = true;
            this.isLinkingLabelDialogueVisible = true;
            this.scannedLabelForAssetId = labelStatus.assetId;
            this.scannedLabelForTeamId = labelStatus.teamId;
            break;
          default:
            this.linkLabelDialogueTitle = 'Unknown Error';
            this.linkLabelDialogueMessage = 'Please try again later';
            this.isLinkingLabelDialogueVisible = true;
        }
      }
    },
    async scannedGateway() {
      if (this.scannedGateway !== null) {
        this.resetLinkingDialogue(true);
        this.linkLabelDialogueTitle = 'Gateway Scanned';
        this.linkLabelDialogueMessage = "You cannot link a gateway to an asset. Click on 'View Zones' if you would like to link a gateway";
        this.isLinkingLabelDialogueVisible = true;
        this.redirectButtonText = 'View Zones';
        this.isRedirectButtonVisible = true;
      }
    },
  },
  created() {
    this.getItemData();
    this.startPolling();
  },
  beforeDestroy() {
    // stop data polling
    this.$store.dispatch('assets/stopPollingAsset');
  },
  methods: {
    startPolling() {
      if (this.$root.featureToggles.includeAssetDataPolling(this.$store.state.mobileApp, this)) {
        this.$store.dispatch('assets/startPollingAsset', {
          id: this.id,
          condition: () => !this.isEditing,
        });
      }
    },
    async getItemData() {
      if (this.resetTemperatureData) {
        this.$store.commit('assets/RESET_TEMPERATURES', this.id);
      }
      if (this.itemAlreadyLoaded) this.loadingDataForItemIds.push(this.id);
      const assetData = await this.$store.dispatch('assets/getAsset', { id: this.id });
      this.loadingDataForItemIds = this.loadingDataForItemIds
        .filter((id) => assetData && id !== assetData.id);
    },
    close() {
      if (this.isFacilityAssetDetail) {
        this.$router.push({
          name: routeNames.facilities.detail,
          query: { tab: facilityTabSlugs.assets },
        });
      } else if (this.isPrintJobAssetDetail) {
        this.$router.push({
          name: routeNames.facilities.printerDetail,
          params: { id: this.$route.params.id, printerId: this.$route.params.printerId },
        });
      } else {
        this.$router.push({ name: routeNames.assets.list, query: this.$route.query });
      }
    },
    redirectClicked() {
      let route = null;
      if (this.scannedGateway !== null) {
        route = { name: routeNames.zones.list };
      } else {
        route = {
          name: routeNames.assets.detail,
          params: {
            id: this.scannedLabelForAssetId,
            teamId: this.scannedLabelForTeamId,
          },
        };
      }
      this.$router.push(route).catch(() => {});
      this.resetLinkingDialogue();
    },
    resetLinkingDialogue(skipStoreReset = false) {
      this.isRedirectButtonVisible = false;
      this.redirectButtonText = 'View Asset';
      this.isYesButtonVisible = false;
      this.isYesButtonLoading = false;
      this.isLinkingLabelDialogueVisible = false;
      this.linkLabelDialogueTitle = '';
      this.linkLabelDialogueMessage = '';
      this.scannedLabelForAssetId = null;
      this.scannedLabelForTeamId = null;
      if (!skipStoreReset) {
        this.$store.commit('mobileApp/RESET_GATEWAY');
        this.$store.commit('mobileApp/RESET_LABEL');
      }
    },
    async replaceCurrentLabel() {
      this.isYesButtonLoading = true;
      await this.addLabelToAsset(this.scannedLabel);
      this.resetLinkingDialogue();
    },
    async save(newName) {
      const oldName = this.asset.name;
      const updatedAsset = await this.$store.dispatch(
        'assets/editAsset',
        { assetId: this.id, name: newName },
      );

      if (!this.hasError) {
        this.$root.trackEvent.editedAsset(updatedAsset, oldName);
      }
    },
    linkLabel() {
      if (this.$store.getters.isMobileApp) {
        this.$store.dispatch('mobileApp/startNfcScan');
      } else {
        this.$router.push({ name: routeNames.assets.linkAssetLabel });
      }
    },
    getLabelTemperatureData() {
      this.$router.push({ name: routeNames.assets.downloadTemperature });
    },
    async deleteAsset() {
      const deletedAsset = this.asset;
      await this.$store.dispatch('assets/removeAsset', this.id);

      if (!this.hasError) {
        this.$root.trackEvent.removedAsset(deletedAsset);
        this.$router.push({ name: routeNames.assets.list });
      }
    },
    async deleteLabel() {
      const labelDeletedFromAsset = this.asset;
      await this.$store.dispatch('assets/removeLabelFromAsset', this.asset);
      this.$root.trackEvent.removedLabel(labelDeletedFromAsset);
    },
    viewNbIotTrackingScreen() {
      // Open the tracking screen in a new tab
      const routeData = this.$router.resolve({
        name: routeNames.nbIot.detail,
        query: { assetId: this.id },
      });
      window.open(routeData.href, '_blank');
    },
  },
};
</script>
